import { useEffect, useState } from "react";
import { useCachedGet } from "@/lib/cache";
import Layout from "@/components/app/Layout";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Briefcase, X, Trash2, Copy, Phone, Video, Code2, Sparkles, Loader2, CheckSquare, Square } from "lucide-react";
import { COUNTRIES, COUNTRY_BY_CODE, fmtSalaryBand, salaryFromInput } from "@/lib/countries";
import { JobCardBody } from "@/components/app/JobCard";
import { JobFilters, useJobFilters, applyJobFilters } from "@/components/app/JobFilters";
import JobsBoard from "@/components/app/JobsBoard";
import JobsList from "@/components/app/JobsList";
import SalaryUnitToggle from "@/components/app/SalaryUnitToggle";
import PhoneLanguageSelect, { normPhoneLang } from "@/components/app/PhoneLanguageSelect";
import PhonePlanEditor, { planCtx, planPayload } from "@/components/app/PhonePlanEditor";
import JdUpload, { applyParsedJd } from "@/components/app/JdUpload";
import FormSection from "@/components/app/FormSection";
import useCloseGuard from "@/lib/useCloseGuard";
import { skillSummary, locationSummary, stageSummary, codingSummary, sectionDone, missingField } from "@/components/app/jobFormSummaries";
import JobSkillsFields from "@/components/app/JobSkillsFields";
import JobClientFields, { jobClientDefaults, jobClientPayload } from "@/components/app/JobClientFields";
import CodingRoundSection, { codingDefaults, codingPayload } from "@/components/app/coding/CodingRoundSection";
import { can } from "@/lib/perm";
import JobScoringEditor from "@/components/app/scoring/JobScoringEditor";
import { scoringSummary, fullScoring } from "@/components/app/scoring/scoringModel";

const DEFAULT_STAGES = { telephonic: true, video: true, coding: true };


const BLANK_JOB = {
  title: "", description: "", must_haves: "", nice_to_haves: "", client: "", account: "",
  country_code: "US", city: "", remote: false, phone_language: "en-US", phone_plan: [],
  salary_currency: "USD", salary_min: "", salary_max: "", salary_unit: "",
  max_notice_days: 60, knockout_questions: "", video_questions: "",
  stages: { ...DEFAULT_STAGES },
  ...codingDefaults(),
  ...jobClientDefaults(),
};

export default function JobsPage() {
  const { user } = useAuth();
  const canBulkDelete = can(user, "records.delete");
  const canCreate = can(user, "jobs.manage");
  const [jobsData, reloadJobs] = useCachedGet("/jobs");
  const jobs = jobsData || [];
  const [flt, setFlt, clearFlt] = useJobFilters();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const st = params.get("status");
    if (st) { setFlt((p) => ({ ...p, q: "", location: "", client: "", status: st })); setParams({}, { replace: true }); }
  }, [params, setFlt, setParams]);
  const shownJobs = applyJobFilters(jobs, flt);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [f, setF] = useState(BLANK_JOB);
  const navigate = useNavigate();
  const [invalidTick, setInvalidTick] = useState(0);
  const requestClose = useCloseGuard(open, f, () => { setOpen(false); setF(BLANK_JOB); setInvalidTick(0); });
  const [genBusy, setGenBusy] = useState(null); // "knockout" | "video" | null

  const load = () => reloadJobs().then(() => setSelected(new Set()));
  const duplicateJob = async (j) => {
    try { const { data } = await api.post(`/jobs/${j.id}/duplicate`); toast.success(`Created draft "${data.title}"`); navigate(`/app/jobs/${data.id}`); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };
  const deleteJob = async (j) => {
    if (!window.confirm(`Move "${j.title}" and its candidates to the Recycle Bin? You can restore it later.`)) return;
    try { await api.delete(`/jobs/${j.id}`); toast.success("Job deleted"); load(); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === jobs.length) setSelected(new Set());
    else setSelected(new Set(jobs.map((j) => j.id)));
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} job${selected.size === 1 ? "" : "s"} and all their candidates? Items can be restored from the Recycle Bin within the retention window.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/jobs/bulk-delete", { job_ids: [...selected] });
      toast.success(`${data.jobs_deleted} job${data.jobs_deleted === 1 ? "" : "s"} deleted · ${data.candidates_affected} candidate${data.candidates_affected === 1 ? "" : "s"} affected`);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  };

  const generateQuestions = async (kind) => {
    const must = f.must_haves.split(",").map(s => s.trim()).filter(Boolean);
    const nice = f.nice_to_haves.split(",").map(s => s.trim()).filter(Boolean);
    if (must.length === 0 && nice.length === 0) {
      toast.error("Please fill the required skills (Must-haves or Nice-to-haves) before generating.");
      return;
    }
    const existing = (kind === "knockout" ? f.knockout_questions : f.video_questions).split("\n").map(s => s.trim()).filter(Boolean);
    setGenBusy(kind);
    try {
      const { data } = await api.post("/jobs/generate-questions", {
        kind, title: f.title, description: f.description,
        must_haves: must, nice_to_haves: nice, existing,
        count: kind === "knockout" ? 4 : 5,
      });
      const seen = new Set(existing.map(q => q.toLowerCase()));
      const fresh = data.questions.filter(q => !seen.has(q.trim().toLowerCase()));
      const combined = [...existing, ...fresh].join("\n");
      setF({ ...f, [kind === "knockout" ? "knockout_questions" : "video_questions"]: combined });
      toast.success(`Added ${fresh.length} new AI-generated question${fresh.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "AI generation failed");
    } finally { setGenBusy(null); }
  };

  const create = async (e, draft = false) => {
    e.preventDefault();
    if (draft && !f.title.trim()) { setInvalidTick((t) => t + 1); toast.error("Add a job title to save a draft"); return; }
    if (!draft && !f.stages.telephonic && !f.stages.video && !f.stages.coding) {
      toast.error("Select at least one interview stage"); return;
    }
    if (!draft && !f.job_type) { toast.error("Select a Job Type (Full Time or Contract)"); return; }
    if (f.salary_currency === "INR" && (f.salary_min || f.salary_max) && !f.salary_unit) { toast.error("Choose a salary unit: LPA or Annual"); return; }
    try {
      await api.post("/jobs", {
        title: f.title,
        description: f.description,
        ...jobClientPayload(f),
        must_haves: f.must_haves.split(",").map(s => s.trim()).filter(Boolean),
        nice_to_haves: f.nice_to_haves.split(",").map(s => s.trim()).filter(Boolean),
        country_code: f.country_code || null,
        city: f.city.trim() || null,
        remote: !!f.remote,
        phone_language: normPhoneLang(f.phone_language),
        phone_plan: planPayload(f.phone_plan),
        salary_currency: f.salary_currency || "USD",
        salary_min: salaryFromInput(f.salary_min, f.salary_unit),
        salary_max: salaryFromInput(f.salary_max, f.salary_unit),
        salary_unit: f.salary_currency === "INR" ? f.salary_unit : "annual",
        max_notice_days: parseInt(f.max_notice_days || 60),
        knockout_questions: f.knockout_questions.split("\n").map(s => s.trim()).filter(Boolean),
        video_questions: f.video_questions.split("\n").map(s => s.trim()).filter(Boolean),
        stages: f.stages,
        ...codingPayload(f),
        ...(f.scoring ? { scoring: fullScoring(f.scoring, f.stages) } : {}),
        status: draft ? "draft" : "open",
      });
      toast.success(draft ? "Draft saved — finish it any time from the Jobs page" : "Job created");
      setOpen(false);
      setF(BLANK_JOB);
      setInvalidTick(0);
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  return (
    <Layout
      actions={
        <>
          {canBulkDelete && jobs.length > 0 && (
            <button
              data-testid="toggle-select-all"
              onClick={toggleAll}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors"
            >
              {selected.size === jobs.length ? <CheckSquare className="w-4 h-4 text-indigo-400" /> : <Square className="w-4 h-4" />}
              {selected.size === jobs.length ? "Clear all" : "Select all"}
            </button>
          )}
          {canBulkDelete && selected.size > 0 && (
            <button
              data-testid="bulk-delete-btn"
              onClick={bulkDelete}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-60"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete {selected.size} selected
            </button>
          )}
          {canCreate && (
          <button data-testid="new-job-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">
            <Plus className="w-4 h-4" /> New Job
          </button>
          )}
        </>
      }
    >
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Roles</div>
        <h1 className="font-display text-3xl font-bold">Jobs</h1>
      </div>

      {jobsData === undefined ? null : jobs.length === 0 ? (
        <div className="tp-card p-12 text-center">
          <Briefcase className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <div className="font-display text-lg mb-1">No jobs yet</div>
          <div className="text-sm text-slate-400 mb-4">Create a role to define its rubric, knockout questions, and start inviting candidates.</div>
          <button data-testid="empty-new-job" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">
            <Plus className="w-4 h-4" /> New Job
          </button>
        </div>
      ) : (
        <>
        <JobFilters jobs={jobs} f={flt} setF={setFlt} clear={clearFlt} shown={shownJobs.length} />
        {shownJobs.length === 0 ? (
          <div className="tp-card p-10 text-center text-sm text-slate-500" data-testid="no-job-matches">
            No jobs match your filters. <button onClick={clearFlt} className="text-indigo-600 font-medium hover:text-indigo-500">Clear filters</button>
          </div>
        ) : flt.view === "board" ? (
          <JobsBoard jobs={shownJobs} onMoved={reloadJobs} />
        ) : flt.view === "list" ? (
          <JobsList jobs={shownJobs} selected={selected} toggleOne={toggleOne} canCreate={canCreate} canDelete={canBulkDelete} onDuplicate={duplicateJob} onDelete={deleteJob} />
        ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="jobs-grid">
          {shownJobs.map((j) => {
            const isSel = selected.has(j.id);
            return (
            <div key={j.id} data-testid={`job-card-${j.id}`} className={`tp-card p-5 hover:border-[color:var(--bd-hi)] hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 relative group flex flex-col ${isSel ? "border-indigo-500/70 ring-1 ring-indigo-500/40" : ""}`}>
              {canBulkDelete && (
                <button
                  type="button"
                  data-testid={`select-job-${j.id}`}
                  aria-pressed={isSel}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleOne(j.id); }}
                  className={`absolute top-4 right-4 z-10 w-6 h-6 rounded-md border grid place-items-center transition-colors ${isSel ? "bg-indigo-600 border-indigo-500 opacity-100" : "border-slate-500 bg-[color:var(--bg-elev)] opacity-0 group-hover:opacity-100"}`}
                  title={isSel ? "Unselect" : "Select for bulk action"}
                >
                  {isSel ? <CheckSquare className="w-4 h-4 text-white" /> : <Square className="w-4 h-4 text-slate-300" />}
                </button>
              )}
              {canCreate && (
              <button
                data-testid={`duplicate-job-${j.id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); duplicateJob(j); }}
                className={`absolute top-3.5 ${canBulkDelete ? "right-20" : "right-11"} p-1.5 rounded opacity-0 group-hover:opacity-100 text-slate-400 hover:text-indigo-500 hover:bg-indigo-500/10 transition-all`}
                title="Duplicate job"
              >
                <Copy className="w-4 h-4" />
              </button>
              )}
              {canBulkDelete && (
                <button
                  data-testid={`delete-job-${j.id}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteJob(j); }}
                className="absolute top-3.5 right-11 p-1.5 rounded opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                title="Delete job"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              )}
              <Link to={`/app/jobs/${j.id}`} className="flex flex-col h-full" data-testid={`job-link-${j.id}`}>
                <JobCardBody j={j} />
              </Link>
            </div>
            );
          })}
        </div>
        )}
        </>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={requestClose}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} onInvalidCapture={() => setInvalidTick((t) => t + 1)} className="tp-card p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-job-form">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Create</div>
                <h2 className="font-display text-2xl font-semibold">New Job</h2>
              </div>
              <button type="button" data-testid="new-job-close" onClick={requestClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid gap-3">
              <FormSection id="basics" done={sectionDone(f).basics} missing={missingField(f, "job").basics} title="Basics" defaultOpen forceOpen={invalidTick} summary={f.title}>
              <JdUpload prefix="job" onParsed={(d) => setF((prev) => applyParsedJd(prev, d))} />
              {[
                ["title", "Job Title", "input", "Senior Backend Engineer"],
                ["description", "Job Description", "textarea", "Build and scale our billing platform…"],
              ].map(([k, l, t, ph]) => (
                <div key={k}>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">{l}</label>
                  {t === "textarea" ? (
                    <textarea data-testid={`job-${k}`} value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})} placeholder={ph} required rows={3} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                  ) : (
                    <input data-testid={`job-${k}`} value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})} placeholder={ph} required={k==="title"} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                  )}
                  {k === "title" && <div className="mt-3"><JobClientFields value={f} onChange={setF} prefix="job" /></div>}
                </div>
              ))}
              </FormSection>
              <FormSection id="skills" done={sectionDone(f).skills} missing={missingField(f, "job").skills} title="Skills" forceOpen={invalidTick} summary={skillSummary(f)}>
              <JobSkillsFields title={f.title} description={f.description} must={f.must_haves} nice={f.nice_to_haves}
                onChange={(p) => setF((prev) => ({ ...prev, ...p }))} prefix="job" />
              </FormSection>
              <FormSection id="location" done={sectionDone(f).location} missing={missingField(f, "job").location} title="Location & Pay" forceOpen={invalidTick} summary={locationSummary(f)}>
              <div className="grid grid-cols-1 sm:grid-cols-[1.3fr_1fr_auto] gap-3 items-end" data-testid="job-location-fields">
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Country</label>
                  <select
                    data-testid="job-country"
                    value={f.country_code}
                    onChange={(e) => {
                      const code = e.target.value;
                      const meta = COUNTRY_BY_CODE[code];
                      // Snap the currency to the country's local currency —
                      // user can still change it after if they want.
                      setF((prev) => ({ ...prev, country_code: code, salary_currency: meta?.currency || prev.salary_currency }));
                    }}
                    className="tp-select w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">City (optional)</label>
                  <input data-testid="job-city" value={f.city} onChange={(e)=>setF({...f,city:e.target.value})} placeholder="Bengaluru / Berlin / Remote" className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-slate-300 pb-2.5 whitespace-nowrap">
                  <input data-testid="job-remote" type="checkbox" checked={!!f.remote} onChange={(e)=>setF({...f,remote:e.target.checked})} className="w-4 h-4" />
                  Remote
                </label>
              </div>
              <div className="grid grid-cols-[1fr_1fr_1fr_0.9fr] gap-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Currency</label>
                  <select data-testid="job-currency" value={f.salary_currency} onChange={(e)=>setF({...f,salary_currency:e.target.value})} className="tp-select w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none">
                    {Array.from(new Set(COUNTRIES.map(c => c.currency))).sort().map((cur) => (
                      <option key={cur} value={cur}>{cur}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Salary Min</label>
                  <input data-testid="job-salary-min" value={f.salary_min} onChange={(e)=>setF({...f,salary_min:e.target.value})} type="number" step="any" placeholder={f.salary_unit === "lpa" ? "20" : ""} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Salary Max</label>
                  <input data-testid="job-salary-max" value={f.salary_max} onChange={(e)=>setF({...f,salary_max:e.target.value})} type="number" step="any" placeholder={f.salary_unit === "lpa" ? "30" : ""} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Max Notice (d)</label>
                  <input data-testid="job-notice" value={f.max_notice_days} onChange={(e)=>setF({...f,max_notice_days:e.target.value})} type="number" className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                </div>
              </div>
              <SalaryUnitToggle form={f} setForm={setF} testid="job-salary-unit" />
              {(f.salary_min || f.salary_max) ? (
                <div className="-mt-1 text-[11px] text-indigo-300" data-testid="salary-preview">
                  Shown as: <span className="font-mono text-slate-300">{fmtSalaryBand(salaryFromInput(f.salary_min, f.salary_unit), salaryFromInput(f.salary_max, f.salary_unit), f.salary_currency, f.salary_unit || "annual")}</span>
                </div>
              ) : null}
              </FormSection>
              <FormSection id="stages" done={sectionDone(f).stages} missing={missingField(f, "job").stages} title="Stages & Questions" forceOpen={invalidTick} summary={stageSummary(f)}>
              <div className="pt-2">
                <label className="text-xs text-slate-400 uppercase tracking-wider">Interview Stages</label>
                <div className="text-[11px] text-slate-500 mt-0.5 mb-2">Pick which rounds this role will run. At least one is required.</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {[
                    { k: "telephonic", label: "Telephonic Interview", Icon: Phone, hint: "AI-led conversational screen" },
                    { k: "video", label: "Video Interview", Icon: Video, hint: "Structured on-camera round" },
                    { k: "coding", label: "Coding Interview", Icon: Code2, hint: "Live coding + proctoring" },
                  ].map(({ k, label, Icon, hint }) => {
                    const on = !!f.stages[k];
                    return (
                      <button
                        type="button"
                        key={k}
                        data-testid={`job-stage-${k}`}
                        aria-pressed={on}
                        onClick={() => setF({ ...f, stages: { ...f.stages, [k]: !on } })}
                        className={`text-left p-3 rounded-lg border transition-colors ${on ? "border-indigo-500/60 bg-indigo-500/10" : "border-[color:var(--bd-sub)] hover:border-[color:var(--bd-hi)] bg-[color:var(--bg-elev)]"}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-4 h-4 rounded border grid place-items-center ${on ? "bg-indigo-600 border-indigo-500" : "border-slate-500"}`}>
                            {on ? <span className="w-2 h-2 bg-white rounded-sm"/> : null}
                          </span>
                          <Icon className={`w-4 h-4 ${on ? "text-indigo-300" : "text-slate-400"}`} />
                          <span className={`text-sm font-medium ${on ? "text-indigo-100" : "text-slate-200"}`}>{label}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 pl-6">{hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              {f.stages?.telephonic && (
                <PhoneLanguageSelect testid="job-phone-language" value={f.phone_language} onChange={(v) => setF((prev) => ({ ...prev, phone_language: v }))} />
              )}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Knockout Questions (one per line)</label>
                  <button type="button" data-testid="gen-knockout-btn" onClick={() => generateQuestions("knockout")} disabled={genBusy !== null} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md border border-indigo-500/40 text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 transition-colors disabled:opacity-50">
                    {genBusy === "knockout" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>} Generate with AI
                  </button>
                </div>
                <textarea data-testid="job-knockouts" value={f.knockout_questions} onChange={(e)=>setF({...f,knockout_questions:e.target.value})} placeholder={"Are you authorized to work in the US?\nAre you comfortable with on-call rotations?"} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Video Questions (one per line)</label>
                  <button type="button" data-testid="gen-video-btn" onClick={() => generateQuestions("video")} disabled={genBusy !== null} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md border border-indigo-500/40 text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 transition-colors disabled:opacity-50">
                    {genBusy === "video" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>} Generate with AI
                  </button>
                </div>
                <textarea data-testid="job-videoq" value={f.video_questions} onChange={(e)=>setF({...f,video_questions:e.target.value})} placeholder="Optional - defaults are used if empty" rows={3} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
              </div>
              {f.stages.telephonic && <PhonePlanEditor value={f.phone_plan} onChange={(v) => setF((prev) => ({ ...prev, phone_plan: v }))} ctx={planCtx(f, salaryFromInput)} />}
              </FormSection>
              {f.stages.coding && (
                <FormSection id="coding" done={sectionDone(f).coding} missing={missingField(f, "job").coding} title="Coding Round" forceOpen={invalidTick} summary={codingSummary(f)}>
              {f.stages.coding && <CodingRoundSection value={f} onChange={setF} ctx={{ title: f.title, description: f.description, must_haves: f.must_haves }} prefix="job" />}
                </FormSection>
              )}
              <FormSection id="scoring" title="Job Scoring" summary={scoringSummary(f.scoring, f.stages)}>
                <JobScoringEditor value={f.scoring} onChange={(v) => setF((prev) => ({ ...prev, scoring: v }))} stages={f.stages} prefix="job" />
              </FormSection>
            </div>
            <div data-testid="job-form-actions" className="sticky -bottom-6 -mx-6 -mb-6 mt-6 px-6 py-3 flex justify-end gap-2 bg-[color:var(--bg-card,#fff)] border-t border-[color:var(--bd-sub)] z-20">
              <button type="button" data-testid="new-job-cancel" onClick={requestClose} className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors">Cancel</button>
              <button type="button" data-testid="save-job-draft" onClick={(e) => create(e, true)} className="px-4 py-2 text-sm rounded-lg border border-[color:var(--bd-sub)] hover:border-indigo-400 transition-colors">Save as draft</button>
              <button data-testid="submit-new-job" type="submit" className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">Create Job</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
